import EverTrue from 'app';
import PropTypes from 'prop-types';
import InteractionFormBody from './interaction-form-body';
import { DynamicForm } from '@evertrue/sdk';
import { useIdentity } from 'base/identity-resolver';
import { InteractionFormController } from 'apps/interactions/form/interaction-form-controller';
import { useInteractionFormContext } from '../context/interaction-form-context';

const InteractionFormConsumer = ({
  editing = false,
  existingInteraction = {},
  contact = {},
  contactOptions = [],
  referrer = '',
  requestHide = () => {},
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  afterSubmit = () => {},
}) => {
  const {
    rules,
    interactionSolicitorLimit,
    interactionStandardFieldsConfig,
    interactionDescriptionRichTextEditor,
    secondaryTargetLimit,
    hasCustomFields,
    orgCustomFields,
    hasInteractionWrites,
  } = useInteractionFormContext();

  /**
   * Sets the initial values for the form based on whether we are editing an existing interaction or creating a new one.
   * @returns {object} - The initial values for the form.
   * If editing, it parses the existing interaction; otherwise, it sets default values.
   * If an existing interaction is provided, it merges that data with the default values.
   */
  const setInitialValues = () => {
    if (editing) return InteractionFormController.parseExistingInteraction(existingInteraction);

    return {
      target_id: contact?.id || '',
      ...InteractionFormController.defaultInteraction(hasCustomFields, orgCustomFields, hasInteractionWrites),
      ...existingInteraction, // Merge existing interaction data if available
    };
  };

  const { affiliation } = useIdentity();
  /**
   * Handles the form submission for creating or editing an interaction.
   * It checks if the user is affiliated with the organization before allowing submission.
   * If the user is not affiliated, it shows an error message.
   * If the submission is successful, it updates the state with the new interaction and hides the form.
   * @param {object} values - The form values submitted by the user.
   * @param {object} formikBag - The Formik bag containing form methods and state.
   * @returns {Promise<void>}
   * @throws {Error} If the user is not affiliated with the organization.
   */
  const handleSubmit = async (values, formikBag) => {
    if (!affiliation && !EverTrue?.config?.isDevelopment()) {
      return EverTrue.Alert.error('Sorry, you must be affiliated with this organization to save an interaction.');
    }
    if (referrer) EverTrue.track.setReferrer(referrer);

    try {
      const newInteraction = await InteractionFormController.submit(values);

      if (typeof afterSubmit === 'function') {
        await afterSubmit(newInteraction);
      }
      requestHide();
    } catch (err) {
      EverTrue.Alert.error(`Failed to save interaction: ${err.message}`);
    }
  };

  /**
   * Depending on the interaction type, we need to validate different fields.
   * This function returns the fields that should be validated based on the interaction type.
   * @param {object} values -- an object containing the interaction values
   * @returns {Array} -- an array of field names to validate
   */
  const fieldsToValidate = (values) => {
    if (values?.interaction_type === InteractionFormController.commentType)
      return InteractionFormController.commentFields;
    if (values?.interaction_type === InteractionFormController.dxoAskType)
      return InteractionFormController.dxoAskFields;
    else return InteractionFormController.standardFieldsWithCustomFields(hasCustomFields, orgCustomFields);
  };

  return (
    <DynamicForm
      type="interaction"
      rules={rules}
      additionalRules={InteractionFormController.getAdditionalRules(
        secondaryTargetLimit,
        interactionSolicitorLimit,
        interactionStandardFieldsConfig,
        interactionDescriptionRichTextEditor
      )}
      fieldsToValidate={fieldsToValidate}
      initialValues={setInitialValues()}
      handleSubmit={handleSubmit}
    >
      <InteractionFormBody
        contact={contact}
        contactOptions={contactOptions}
        referrer={referrer}
        requestHide={requestHide}
        saveLabel={saveLabel}
        cancelLabel={cancelLabel}
      />
    </DynamicForm>
  );
};

InteractionFormConsumer.propTypes = {
  editing: PropTypes.bool,
  existingInteraction: PropTypes.object,
  contact: PropTypes.object,
  contactOptions: PropTypes.array,
  referrer: PropTypes.string,
  requestHide: PropTypes.func.isRequired,
  saveLabel: PropTypes.any,
  cancelLabel: PropTypes.any,
  afterSubmit: PropTypes.func,
};

export default InteractionFormConsumer;
